import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { Container } from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { format, zonedTimeToUtc } from 'date-fns-tz';
import { IEventDetailsDTO } from '../../../../api/events.dao';
import {
    ConnectionType,
    IConnectionDetailsDTO,
    IConnectionsDAO,
    IDeleteConnectionRequestParams,
    IKnovioLiveStreamDTO,
    IMeetingConnectionRequestParams,
    IMeetingConnectionsDTO,
    IMeetingConnectionsRequestParams,
    IRoomConnectionRequestParams,
    IRoomConnections,
    IRoomMeetingConnectionsDTO,
    IUpdateSingleMeetingPayload,
} from '../../../../api/connections.dao';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { CLONED_CONNECTION, ConnectionVersion } from '../../../../constants/common.constants';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { ILocalStorageService } from '../../../../infrastructure/local-storage-service';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { utcToZonedTime } from '../../../../infrastructure/utils/date-fns-overrides.utils';
import { DateFnsFormats } from '../../../../infrastructure/date-formatter';
import { NAVIGATION_CONTROLS } from '../../../../constants/pagination.contants';
import { AttendeeRole } from '../../../../api/attendees.dao';
import { ConnectionForm } from '../shared/connections-list/_connection-form/connection-form';
import { alertServerError, showNotification } from '../../../global/modal/alerts-host/alert-templates';
import { IEditMeetingConnectionDetailsProps, ISubWindowFormMethods } from '../manage-event.viewmodel';
import { ZoomMeetingVM } from '../shared/connections-list/_zoom-meeting-modal/zoom-meeting.viewmodel';
import { WebinarReportsViewModel } from '../shared/webinar-report/webinar-reports.viewmodel';
import { MeetingConnectionsForm } from './_meeting-connections-form/meeting-connections-form.presentation';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { IEventRequestParams } from '../../../../api/shared.dto';
import { OperatorsBulkUpdateForm } from './operators-bulk-update-form/operators-bulk-update-form';
import { IRouter } from '../../../../infrastructure/router';
import { RouteParams, Routes } from '../../../../routes-table';
import { getOrganizationsForSpeakersSync, isPathIncludesInCurrentRoute } from '../../../../infrastructure/utils/common.utils';
import { IUsersDAO } from '../../../../api/users.dao';
import { IOperatorsDTO, IUserOperatorDTO, OperatorRole } from '../../../../api/operators.dao';
import { IOperatorDetailsDTO } from '../../../../api/rooms.dao';
import { IGlobalStorage } from '../../../../infrastructure/global-storage';
import { CreateZoomWebinarVM } from '../shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar.viewmodel';
import { EventExecutionRuntime } from '../../../../domain/jobs/implementations/event-execution-runtime';
import { JobType } from '../../../../api/jobs.dao';
import { IDeleteConnectionDetails } from '../shared/connections-list/_connection-delete-confirm-popover';
import { UniqueJoiningLInksAlertsPopup } from '../../../../infrastructure/utils/unique-joining-lInks-alerts-popup.viewmodel';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { KnovioLiveStreamVariant, KnovioLiveStreamVM, KnovioLiveStreamVMProps } from '../shared/connections-list/knovio-live-stream-modal/knovio-live-stream.viewmodel';
import { IConfigurePassportDisplayFormProps } from '../shared/configure-passport-display/configure-passport-display.viewmodel';
import { panelistDeletionToastMessage } from '../../../../constants/panelist-link-connection.constants';
import { trackMixPanelPanelistDeletion } from '../../../../infrastructure/utils/connections.utils';

type EditConnectionMethodType = (
    params: (IMeetingConnectionRequestParams | IRoomConnectionRequestParams) & IConnectionDetailsDTO
) => Promise<IConnectionDetailsDTO>;
export type DeleteConnectionMethodType = (
    params: (IMeetingConnectionRequestParams | IRoomConnectionRequestParams) & IDeleteConnectionRequestParams
) => Promise<IMeetingConnectionsDTO | void>;
export type ExtraPropsType = { roomId: number } | { meetingId: number };

class ConnectionsViewModel {
    @mobx.observable
    private roomsListWithConnections: IRoomConnections[];
    @mobx.observable
    public filterDates: Date[];
    @mobx.observable
    public selectedFilterDate: Date | null;
    @mobx.observable
    private loading: boolean;
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    private zoomMeetingVM: ZoomMeetingVM;
    private webinarMeetingVM: CreateZoomWebinarVM;
    private webinarReportsVM: WebinarReportsViewModel;
    private knovioLiveStreamVM?: KnovioLiveStreamVM;
    @mobx.observable
    private meetingIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    @mobx.observable
    private connectionIdToEditPopupMap: { [key: string]: ISubWindowFacade };
    @mobx.observable
    private editRegistrantPopupIds: { [key: string]: () => void } = {};
    public handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void;
    public trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;

    public showEventNotesPopup: (isEditMode: boolean, openFrom: OpenFrom) => void;
    public showConfigurePassportForm: (props: IConfigurePassportDisplayFormProps, connection: IConnectionDetailsDTO) => void;
    @mobx.observable
    public guidelinesFormSubWindow: { [key: string]: ISubWindowFacade };
    @mobx.observable
    public isDownloadAllEventConnectionsModalOpen: boolean;
    @mobx.observable
    public selectedMeetingIds: number[] = [];
    @mobx.observable
    public bulkOperatorsUpdateFormPopupMap: ISubWindowFacade;
    @mobx.observable
    public operatorsListToAdd: IUserOperatorDTO[];
    @mobx.observable
    public isKnovioPreCheckLoading: boolean;

    constructor(
        public readonly accountId: number,
        private readonly _event: IEventDetailsDTO,
        private readonly intl: IntlShape,
        private readonly subWindowFormMethods: ISubWindowFormMethods,
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = Container.get(IConnectionsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly localeStorageService: ILocalStorageService = IoC.Container.get(ILocalStorageService),
        @IoC.Inject private readonly routingService: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly _usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly executionRuntime: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
    ) {
        this.meetingIdToSubWindowMap = {};
        this.connectionIdToEditPopupMap = {};
        this.roomsListWithConnections = [];
        this.filterDates = [];
        this.selectedFilterDate = null;
        this.event = _event;
        this.isKnovioPreCheckLoading = false;
        this.initWebinarReportsVM();
        this.initKnovioLiveStreamModal();

        this.executionRuntime.onComplete(JobType.EventBlueprintTrigger, async () => await this.fetchRoomsWithConnections());
        this.executionRuntime.onComplete(JobType.GenerateConnectionsBlueprintTrigger, async () => await this.fetchRoomsWithConnections());
    }

    public get isUniqueJoiningLinksDeletionTriggerEnabled(): boolean {
        return this.executionRuntime.getJobByType(JobType.DeleteUniqueJoiningLinks).triggerEnabled;
    }

    private get eventRequestParams(): Exclude<IEventRequestParams, 'timeZone'> {
        return { accountId: this.accountId, eventId: this.event.eventId };
    }

    @mobx.action
    public async getConnectionDatesList() {
        this.loading = true;

        try {
            const { dates } = await this.connectionsDAO.getConnectionDatesList(this.eventRequestParams);
            let zonedPrevDay;
            const filterDates = [];
            const dayNow = zonedTimeToUtc(
                format(new Date(), DateFnsFormats.FullDate).slice(0, 10),
                this.event.timeZone
            );

            dates.forEach(date => {
                const meetingDate = format(
                    utcToZonedTime(new Date(date), this.event.timeZone),
                    DateFnsFormats.FullDate
                );
                const zonedCurrentDay = zonedTimeToUtc(meetingDate.slice(0, 10), this.event.timeZone);
                if (!zonedPrevDay || !isSameDay(zonedPrevDay, zonedCurrentDay)) {
                    filterDates.push(zonedCurrentDay);
                    zonedPrevDay = zonedCurrentDay;
                }
            });

            mobx.runInAction(() => {
                this.filterDates = filterDates;
                this.selectedFilterDate =
                    this.filterDates.find(d => isSameDay(d, this.selectedFilterDate)) ||
                    this.filterDates.find(d => isSameDay(d, dayNow) || isAfter(d, dayNow)) || this.filterDates[0];
            });
            this.fetchRoomsWithConnections();
        } catch (error) {
            this.errorParsingService.extractMessage(error);
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    }

    public getLoading() {
        return this.loading;
    }

    public initKnovioLiveStreamModal(): void {
        if (!hasPermission(Permission.EDIT_KNOVIO_LIVE_STREAMS)) {
            return;
        }

        const props: KnovioLiveStreamVMProps = {
            startDateTime: new Date(),
            timeZone: this.event.timeZone,
            meetingApiParams: {
                accountId: this.accountId,
                eventId: this.event.eventId
            },
        };

        this.knovioLiveStreamVM = new KnovioLiveStreamVM(props);
    }

    public initZoomMeetingVM() {
        this.zoomMeetingVM = new ZoomMeetingVM(
            this.event.timeZone, null, new Date(),
            new Date(), null, this.accountId,
            this.event.eventId,
        );
    }

    public initWebinarMeetingVM(): void {
        this.webinarMeetingVM = new CreateZoomWebinarVM(
            this.event.timeZone,
            '',
            new Date(),
            new Date(),
            null,
            { eventId: this.event.eventId, accountId: this.accountId }
        );
    }

    public getKnovioLiveStreamModalVM(): KnovioLiveStreamVM {
        return this.knovioLiveStreamVM;
    }

    public getWebinarMeetingVM(): CreateZoomWebinarVM {
        return this.webinarMeetingVM;
    }

    public initWebinarReportsVM() {
        this.webinarReportsVM = new WebinarReportsViewModel(this.accountId, this.event.eventId, OpenFrom.Connections);
    }

    public getZoomMeetingVM(): ZoomMeetingVM {
        return this.zoomMeetingVM;
    }

    public getWebinarReportsVM(): WebinarReportsViewModel {
        return this.webinarReportsVM;
    }

    public setHandleTroubleshoot = (handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void): void => {
        this.handleTroubleshoot = handleTroubleshoot;
    };

    public setTrackHostJoinClick = (trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void): void => {
        this.trackHostJoinClick = trackHostJoinClick;
    };

    @mobx.action
    public fetchRoomsWithConnections = async () => {
        this.loading = true;
        try {
            const result = await this.connectionsDAO.getRoomsConnectionsList({
                ...this.eventRequestParams,
                startDateTime: this.selectedFilterDate
            });
            mobx.runInAction(() => {
                this.roomsListWithConnections = result;
            });
        } catch (error) {
            this.errorParsingService.extractMessage(error);
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };

    @mobx.action
    public fetchMeetingsWithConnectionsByRoomId = async (roomConnections: IRoomConnections) => {
        const params: IMeetingConnectionsRequestParams = { ...this.eventRequestParams };
        if (roomConnections.roomId) {
            params.roomId = roomConnections.roomId;
        } else {
            params.startDateTime = this.selectedFilterDate;
        }
        try {
            const result = await this.connectionsDAO.getMeetingConnectionsByRoom(params);
            mobx.runInAction(() => {
                roomConnections.meetings = result;
            });
        } catch (error) {
            this.errorParsingService.extractMessage(error);
        }
    };

    public getRoomsConnections() {
        return this.roomsListWithConnections;
    }

    public getEvent() {
        return this.event;
    }

    public isEditFormShown(meetingId: number) {
        return (
            this.meetingIdToSubWindowMap[meetingId] &&
            this.meetingIdToSubWindowMap[meetingId].getStatus() !== SubWindowStatus.Closed
        );
    }

    @mobx.action
    public updateConnections = (meetingId: number, openFrom?: OpenFrom) => {
        const existingSubWindowForThisConnection = this.meetingIdToSubWindowMap[meetingId];

        if (
            existingSubWindowForThisConnection &&
            existingSubWindowForThisConnection.getStatus() !== SubWindowStatus.Closed
        ) {
            // if we successfully maximized existing one
            existingSubWindowForThisConnection.maximize();
        } else {
            this.meetingIdToSubWindowMap[meetingId] = this.subWindowService.showSubWindow({
                title: 'Edit Meeting Connections',
                subWindowClassname: 'relative',
                componentToHost: MeetingConnectionsForm,
                componentToHostProps: {
                    event: this.event,
                    accountId: this.accountId,
                    meetingId: meetingId,
                    saveCompletedCallback: this.fetchRoomsWithConnections,
                    subWindowFormMethods: this.subWindowFormMethods,
                    closeSubWindow: this.closeSubWindow,
                    editConnection: this.editMeetingConnection,
                    openFrom: openFrom,
                    onClosePopupHandler: callback => {
                        this.editRegistrantPopupIds[meetingId] = callback;
                    },
                    handleTroubleshoot: (connection: IConnectionDetailsDTO) => {
                        this.handleTroubleshoot(connection, OpenFrom.EditMeetingConnections);
                    },
                    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => {
                        this.showConfigurePassportForm({
                            accountId: this.accountId,
                            eventId: this.event.eventId,
                            meetingId: meetingId,
                            meetingConnectionId: connection.meetingConnectionId,
                        }, connection);
                    },
                    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => {
                        this.trackHostJoinClick(eventName, OpenFrom.EditMeetingConnections);
                    },
                    closeMeetingConnectionPopUp: () => {
                        this.closeMeetingConnectionPopUp(meetingId);
                    },
                },
                onCloseIconClickHandler: () => {
                    if (meetingId in this.editRegistrantPopupIds) {
                        this.editRegistrantPopupIds[meetingId]();
                    }
                    this.meetingIdToSubWindowMap[meetingId].close();
                }
            });
        }
    };

    public closeSubWindow = meetingId => {
        if (meetingId) {
            this.meetingIdToSubWindowMap[meetingId].close();
        }
    };

    @mobx.action
    public handleChangeFilterDate = (date: Date = undefined) => {
        this.selectedFilterDate = date;
        if (this.selectedFilterDate) {
            this.fetchRoomsWithConnections();
        }
    };

    @mobx.action
    public handleChangeDate = (action: string) => {
        const selectedDateIndex = this.filterDates.indexOf(this.selectedFilterDate);
        switch (action) {
            case NAVIGATION_CONTROLS.Previous:
                this.selectedFilterDate = this.filterDates[selectedDateIndex - 1];
                break;
            case NAVIGATION_CONTROLS.Next:
                this.selectedFilterDate = this.filterDates[selectedDateIndex + 1];
                break;
            default:
                break;
        }
        this.fetchRoomsWithConnections();
    };

    @mobx.action
    public toggleRoomMeetings = async (roomConnections: IRoomConnections) => {
        if (roomConnections.meetingIds?.length) {
            roomConnections.isExpanded = !roomConnections.isExpanded;
            if (roomConnections.isExpanded && !roomConnections.meetings) {
                await this.fetchMeetingsWithConnectionsByRoomId(roomConnections);
            }
        }
    };

    public downloadConnectionsCSV = () => {
        this.connectionsDAO.getConnectionsCSV(this.eventRequestParams);
    };

    public getInheritedConnection = (
        roomDetails: IRoomConnections,
        role: string,
        connection: IConnectionDetailsDTO,
        meetingDetails?: IRoomMeetingConnectionsDTO
    ): IConnectionDetailsDTO | undefined => {
        const lookupConnections = [
            ...(meetingDetails?.connections ?? []),
            ...(meetingDetails?.connectionGroups ?? []),
            ...(meetingDetails?.defaultConnection ? [meetingDetails.defaultConnection] : []),
            ...(roomDetails.connections ?? []),
            ...(roomDetails.connectionGroups ?? [])
        ];
        const findConnectionById = (connectionId: number | null | undefined) => {
            if (!connectionId) {
                return undefined;
            }

            return lookupConnections.find(c => c.meetingConnectionId === connectionId);
        };

        const meetingRoleConnection = findConnectionById(
            this.getMeetingRoleConnectionIdByAttendeeRole(role, meetingDetails)
        );
        if (meetingRoleConnection) {
            return meetingRoleConnection;
        }

        const roomRoleConnection = findConnectionById(
            this.getRoomRoleConnectionIdByAttendeeRole(role, roomDetails)
        );
        if (roomRoleConnection) {
            return roomRoleConnection;
        }

        if (connection) {
            return connection;
        }

        return findConnectionById(roomDetails.defaultConnectionId);
    };

    private getMeetingRoleConnectionIdByAttendeeRole = (
        role: string,
        meetingDetails?: IRoomMeetingConnectionsDTO
    ): number | null | undefined => {
        if (!meetingDetails) return undefined;
        if (role === AttendeeRole.Presenter) return meetingDetails.presenterRoleConnectionId;
        if (role === AttendeeRole.Host) return meetingDetails.hostRoleConnectionId;
        if (role === AttendeeRole.Support) return meetingDetails.supportRoleConnectionId;
        return undefined;
    };

    private getRoomRoleConnectionIdByAttendeeRole = (
        role: string,
        roomDetails: IRoomConnections
    ): number | null => {
        if (role === AttendeeRole.Presenter) return roomDetails.presenterRoleConnectionId;
        if (role === AttendeeRole.Host) return roomDetails.hostRoleConnectionId;
        return null;
    };

    public editRoomConnection = (roomId: number, connection: IConnectionDetailsDTO, onSuccess?: () => void, shouldSetUpdateCallback?: boolean) => {
        this.editConnection({ roomId: roomId }, connection, this.connectionsDAO.editRoomConnection, onSuccess, shouldSetUpdateCallback);
    };

    public handleEditRoomConnection = (roomId: number, connection: IConnectionDetailsDTO) => {
        this.editConnection({ roomId: roomId }, connection, this.connectionsDAO.editRoomConnection, () =>
            this.getConnectionDatesList()
        );
    };



    // handle edit connection from Control tab
    public editMeetingConnection = (
        meetingId: number,
        connection: IConnectionDetailsDTO,
        onSuccess?: (updatedDetails?: IConnectionDetailsDTO) => void,
        shouldSetUpdateCallback?: boolean,
        shouldShowKnovioReplayToggle?: boolean,
        editMeetingConnectionProps?: IEditMeetingConnectionDetailsProps,
    ) => {
        this.editConnection(
            { meetingId: meetingId },
            connection,
            this.connectionsDAO.editMeetingConnection,
            onSuccess,
            shouldSetUpdateCallback,
            shouldShowKnovioReplayToggle,
            editMeetingConnectionProps,
        );
    };

    // handle edit connection from Connections tab
    public handleEditMeetingConnection = (meetingId: number, connection: IConnectionDetailsDTO, roomMeetingConnections: IRoomMeetingConnectionsDTO) => {
        const shouldShowKnovioReplayToggle = (connection.type === ConnectionType.Knovio || connection.type === ConnectionType.Other)
            && (
                !roomMeetingConnections.replayConnection
                || connection.meetingConnectionId === roomMeetingConnections.replayConnection?.meetingConnectionId
            )
            ;

        const organizationsForSpeakersSync = getOrganizationsForSpeakersSync(roomMeetingConnections);
        this.editConnection({ meetingId: meetingId }, connection, this.connectionsDAO.editMeetingConnection, () =>
            this.getConnectionDatesList(), true, shouldShowKnovioReplayToggle,
            { organizationsForSpeakersSync: organizationsForSpeakersSync }
        );
    };

    public trackEventMixPanel = (eventGroup: TRACKING_EVENTS, eventName: string): void => {
        this.trackingService.get().track(eventGroup, {
            [eventName]: 'Yes',
            'Open From': OpenFrom.Connections
        });
    };

    public handleCloneConnection = (connection: IConnectionDetailsDTO) => {
        const localeStorageData = {
            name: CLONED_CONNECTION,
            value: connection,
            creationDate: Date.now()
        };
        this.localeStorageService.setLocalStorageValue(localeStorageData);
        this.trackingService.get().track(CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CLONE_CONNECTION, {
            'Open From': OpenFrom.Connections,
        });
    };

    @mobx.computed
    public get isCloneConnectionExist(): boolean {
        return this.localeStorageService.isLocalStorageValueExist(CLONED_CONNECTION);
    }

    private editConnection = (
        extraProps: ExtraPropsType,
        connection: IConnectionDetailsDTO,
        editApiCallMethod: EditConnectionMethodType,
        onSuccess: (updatedDetails?: IConnectionDetailsDTO) => void = () => undefined,
        shouldSetUpdateCallback: boolean = true,
        shouldShowKnovioReplayToggle: boolean = false,
        editConnectionProps?: IEditMeetingConnectionDetailsProps,
    ) => {
        const isEdit = this.zoomMeetingVM.getEditModeStatus();
        if (shouldSetUpdateCallback && connection.type === ConnectionType.Zoom && !isEdit) {
            this.zoomMeetingVM.getZoomDetails(connection);
            this.zoomMeetingVM.setUpdateZoomMeetingCallback((data: IConnectionDetailsDTO) => this.editConnection(
                extraProps,
                // manually assign blueprintId to the connection fetched from the zoom
                { ...data, blueprintId: connection.blueprintId },
                editApiCallMethod,
                onSuccess
            ));
            return;
        }

        if (shouldSetUpdateCallback && connection.type === ConnectionType.Webinar && !this.webinarMeetingVM.isEditMode) {
            this.webinarMeetingVM.openEditWebinarModal(connection);
            this.webinarMeetingVM.setUpdateWebinarMeetingCallback((data: IConnectionDetailsDTO) => this.editConnection(
                extraProps,
                data,
                editApiCallMethod,
                onSuccess
            ));
            return;
        }

        if (connection.type === ConnectionType.Knovio && hasPermission(Permission.EDIT_KNOVIO_LIVE_STREAMS)) {
            this.initKnovioEditLiveStreamModal(
                extraProps,
                connection,
                editApiCallMethod,
                onSuccess,
                shouldShowKnovioReplayToggle,
                editConnectionProps
            );
            return;
        }

        this.editConnectionCallback(
            extraProps,
            connection,
            editApiCallMethod,
            onSuccess,
            shouldShowKnovioReplayToggle,
            editConnectionProps
        );
    };

    private editConnectionCallback = (
        extraProps: ExtraPropsType,
        connection: IConnectionDetailsDTO,
        editApiCallMethod: EditConnectionMethodType,
        onSuccess: (updatedDetails?: IConnectionDetailsDTO) => void = () => undefined,
        shouldShowKnovioReplayToggle: boolean = false,
        editConnectionProps?: IEditMeetingConnectionDetailsProps,
    ): void => {
        const existingSubWindow = this.connectionIdToEditPopupMap[connection.meetingConnectionId];
        let shallOpenNewSubWindow = true;
        if (existingSubWindow && existingSubWindow.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindow.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            this.connectionIdToEditPopupMap[connection.meetingConnectionId] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage({
                    id: 'oe.connection.editDetails',
                    description: 'Edit Popover header text',
                    defaultMessage: 'Edit Connection Details'
                }),
                componentToHost: ConnectionForm,
                componentToHostProps: {
                    meetingConnectionDetails: connection,
                    meetingApiParams: {
                        eventId: this.event.eventId,
                        accountId: this.accountId,
                        meetingId: 'meetingId' in extraProps ? extraProps['meetingId'] : '',
                    },
                    executeSubmitApiCall: async (connectionDetails: IConnectionDetailsDTO) => {
                        const response = await this.updateConnection(extraProps, connectionDetails, editApiCallMethod);
                        onSuccess(response);
                    },
                    deleteConnection: async (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails) => {
                        if ('meetingId' in extraProps) {
                            await this.handleDeleteMeetingConnection(extraProps['meetingId'], data, onSuccess, connectionDetails);
                        } else {
                            await this.handleDeleteRoomConnection(extraProps['roomId'], data, onSuccess);
                        }
                    },
                    getKnovioConnectionDetails: async (connectionDetails: IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> => {
                        if ('meetingId' in extraProps) {
                            return await this.getKnovioConnectionDetails(extraProps['meetingId'], connectionDetails);
                        } else {
                            return await this.getKnovioRoomConnectionDetails(extraProps['roomId'], connectionDetails);
                        }
                    },
                    shouldShowKnovioReplayToggle: shouldShowKnovioReplayToggle,
                    organizationsForSpeakersSync: editConnectionProps?.organizationsForSpeakersSync,
                    connectionVersion: connection.connectionVersion
                }
            });
        }
    };

    private initKnovioEditLiveStreamModal = (
        extraProps: ExtraPropsType,
        connection: IConnectionDetailsDTO,
        editApiCallMethod: EditConnectionMethodType,
        onSuccess: (updatedDetails?: IConnectionDetailsDTO) => void = () => undefined,
        shouldShowKnovioReplayToggle: boolean = false,
        editConnectionProps?: IEditMeetingConnectionDetailsProps,
    ): void => {
        this.setKnovioPreCheckLoadingState(true);
        const id = 'meetingId' in extraProps ? extraProps['meetingId'] : extraProps['roomId'];

        this.getKnovioConnectionDetails(id, connection)
            .then(response => {
                if (response.knovioEditMetadata) {
                    const afterUpdateCallback = (dto: IKnovioLiveStreamDTO) => {
                        this.editConnectionCallback(
                            extraProps,
                            { ...connection, knovioName: dto.title },
                            editApiCallMethod,
                            onSuccess,
                            shouldShowKnovioReplayToggle,
                            editConnectionProps,
                        );
                    };
                    this.knovioLiveStreamVM.setKnovioConnectionCallback(this.editConnectionCallback.bind(
                        this,
                        extraProps,
                        connection,
                        editApiCallMethod,
                        onSuccess,
                        shouldShowKnovioReplayToggle,
                        editConnectionProps,
                        response
                    ));
                    this.knovioLiveStreamVM.setKnovioAfterUpdateCallback(afterUpdateCallback);
                    this.knovioLiveStreamVM.setKnovioMetadata(response.knovioEditMetadata);
                    this.knovioLiveStreamVM.setMeetingConnectionId(connection.meetingConnectionId);
                    this.knovioLiveStreamVM.setKnovioPresentationId(response.knovioPresentationId);
                    this.knovioLiveStreamVM.setBehavior(KnovioLiveStreamVariant.EDIT);
                    this.knovioLiveStreamVM.openModal();
                } else {
                    this.editConnectionCallback(
                        extraProps,
                        connection,
                        editApiCallMethod,
                        onSuccess,
                        shouldShowKnovioReplayToggle,
                        editConnectionProps,
                    );
                }
            }).catch(() => {
                this.editConnectionCallback(
                    extraProps,
                    connection,
                    editApiCallMethod,
                    onSuccess,
                    shouldShowKnovioReplayToggle,
                    editConnectionProps
                );
            })
            .finally(() => {
                this.setKnovioPreCheckLoadingState(false);
            });
    };

    public getKnovioRoomConnectionDetails = async (roomId: number, connection: IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> => {
        return await this.connectionsDAO.getKnovioRoomConnectionDetails({
            roomId: roomId,
            meetingConnectionId: connection.meetingConnectionId,
            accountId: this.accountId,
            eventId: this.event.eventId
        });
    };

    public getKnovioConnectionDetails = async (meetingId: number, connectionDetails: IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> => {
        return await this.connectionsDAO.getKnovioConnectionDetails({
            ...{ meetingConnectionId: connectionDetails.meetingConnectionId },
            accountId: this.accountId,
            eventId: this.event.eventId,
            meetingId: meetingId,
        });
    };


    private updateConnection = async (
        extraProps: ExtraPropsType,
        connectionDetails: IConnectionDetailsDTO,
        editApiCallMethod: EditConnectionMethodType
    ) => {
        return await editApiCallMethod({
            ...connectionDetails,
            ...extraProps,
            ...this.eventRequestParams
        });
    };

    public handleDeleteMeetingConnection = (
        meetingId: number,
        data: IDeleteConnectionRequestParams,
        onSuccess?: (updatedDetails?: IConnectionDetailsDTO) => void,
        connectionDetails?: IDeleteConnectionDetails
    ): void => {
        this.deleteMeetingConnection(
            meetingId,
            data,
            onSuccess ? onSuccess : () => this.getConnectionDatesList(),
            connectionDetails
        );
    };

    public handleDeleteRoomConnection = async (
        roomId: number,
        data: IDeleteConnectionRequestParams,
        onSuccess?: (updatedDetails?: IConnectionDetailsDTO) => void,
    ) => {
        this.deleteRoomConnection(roomId, data, onSuccess ? onSuccess : () => this.getConnectionDatesList());
    };

    private deleteConnection = async (
        extraProps: ExtraPropsType,
        data: IDeleteConnectionRequestParams,
        deleteApiCallMethod: DeleteConnectionMethodType,
        onSuccess: () => void = () => undefined
    ) => {
        const {
            meetingConnectionId,
            zoomMeetingDelete = false,
            webinarMeetingDelete = false,
            audioBridgeMeetingDelete = false,
            panelistMeetingDelete = false,
        } = data;
        const existingSubWindow = this.connectionIdToEditPopupMap[meetingConnectionId];
        if (existingSubWindow && existingSubWindow.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindow.close();
        }
        try {
            await deleteApiCallMethod.call(this.connectionsDAO, {
                ...extraProps,
                ...this.eventRequestParams,
                meetingConnectionId: meetingConnectionId,
                webinarMeetingDelete: webinarMeetingDelete,
                audioBridgeMeetingDelete: audioBridgeMeetingDelete,
                zoomMeetingDelete: zoomMeetingDelete,
                panelistMeetingDelete: panelistMeetingDelete,
                joinMethodId: data.joinMethodId
            });
            this.trackEventMixPanel(
                TRACKING_EVENTS.DELETE_CONNECTION,
                CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.DELETE_CONNECTION
            );
            onSuccess();
        } catch (error) {
            alertServerError('API error deleting an connection', this.errorParsingService.extractMessage(error));
        }
    };

    public deleteRoomConnection = async (roomId: number, data: IDeleteConnectionRequestParams, onSuccess?: () => void) => {
        this.deleteConnection({ roomId: roomId }, data, this.connectionsDAO.deleteRoomConnection, onSuccess);
    };

    public deleteMeetingConnection = async (
        meetingId: number,
        data: IDeleteConnectionRequestParams,
        onSuccess?: () => void,
        connectionDetails?: IDeleteConnectionDetails
    ) => {
        const isV2Panelist = data.connectionVersion === ConnectionVersion.V2 && data.isPanelistLink;
        await this.deleteConnection({ meetingId: meetingId }, data,
            isV2Panelist
                ? this.connectionsDAO.deleteChildConnection
                : this.connectionsDAO.deleteMeetingConnection,
            onSuccess);
        if (data.isPanelistLink) {
            trackMixPanelPanelistDeletion(TRACKING_EVENTS.DELETE_PANELIST_LINK, meetingId);
            showNotification(panelistDeletionToastMessage);
        };
        if (connectionDetails?.isConnectionAutoSync) {
            const uniqueJoiningLinksQuantity = 7;
            const commonAlertsPopup = new UniqueJoiningLInksAlertsPopup(
                meetingId,
                () => {
                    this.closeMeetingConnectionPopUp(meetingId);
                },
                this.checkRouteAfterBulkUpdate
            );
            commonAlertsPopup.handleRemoveUniqueJoiningLinks(uniqueJoiningLinksQuantity, connectionDetails.type);
        }
    };

    @mobx.action
    private setKnovioPreCheckLoadingState = (state: boolean): void => {
        this.isKnovioPreCheckLoading = state;
    };

    @mobx.action
    public setDownloadAllEventConnectionsModalOpen(value: boolean): void {
        this.isDownloadAllEventConnectionsModalOpen = value;
    }

    public trackDownloadOperatorsEvent(): void {
        this.trackConnectionsTabMixPanelEvent(TRACKING_EVENTS.DOWNLOAD_OPERATORS);
    }
    public trackDownloadRoomConnections(): void {
        this.trackConnectionsTabMixPanelEvent(TRACKING_EVENTS.DOWNLOAD_ROOM_CONNECTIONS);
    }

    private trackConnectionsTabMixPanelEvent(eventName: TRACKING_EVENTS, eventData: object = {}): void {
        void this.trackingService.get().track(eventName, {
            'Open From': OpenFrom.Connections,
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Event ID': this.eventRequestParams.eventId,
            ...eventData
        });
    }

    @mobx.computed
    public get staffMeetingsPopUpTitle(): string {
        return this.intl.formatMessage({
            id: `oe.meeting.staffBulkMeeting${this.selectedMeetingIds.length > 1 ? 's' : ''}`,
            defaultMessage: `Staff {prefix} Meeting${this.selectedMeetingIds.length > 1 ? 's' : ''}`,
            description: 'Button text'
        }, {
            prefix: this.selectedMeetingIds.length
        });
    }

    @mobx.action
    public openOperatorsBulkPopup(): void {
        if (
            !this.bulkOperatorsUpdateFormPopupMap ||
            this.bulkOperatorsUpdateFormPopupMap?.getStatus() === SubWindowStatus.Closed
        ) {

            this.bulkOperatorsUpdateFormPopupMap = this.subWindowService.showSubWindow({
                className: 'meeting-operators-update',
                componentToHost: OperatorsBulkUpdateForm,
                componentToHostProps: {
                    intl: this.intl,
                    meetingIds: this.selectedMeetingIds,
                    saveBulkCompletedCallback: () => this.checkRouteAfterBulkUpdate(),
                    eventRequestParams: this.eventRequestParams
                },
                title: this.staffMeetingsPopUpTitle,
            });
        } else {
            this.bulkOperatorsUpdateFormPopupMap.maximize();
        }
    }

    public isSingleMeetingSelected(meetingId: number): boolean {
        return this.selectedMeetingIds.includes(meetingId);
    }

    @mobx.action.bound
    public handleSelectSingleMeeting(meetingId: number): void {
        if (!this.selectedMeetingIds.includes(meetingId)) {
            this.selectedMeetingIds = [...new Set([...this.selectedMeetingIds, meetingId])];
        } else {
            this.selectedMeetingIds = this.selectedMeetingIds.filter(m => m !== meetingId);
        }
    }

    @mobx.action.bound
    public handleSelectRoom(roomConnections: IRoomConnections): void {
        const meetingIds = this.getMeetingIdsOnRoom(roomConnections);
        if (this.isRoomSelected(roomConnections)) {
            this.selectedMeetingIds = this.selectedMeetingIds
                .filter(i => !meetingIds.includes(i));
        } else {
            this.selectedMeetingIds = [...new Set([...this.selectedMeetingIds, ...meetingIds])];
        }
    }

    public isRoomSelected(roomConnections: IRoomConnections): boolean {
        let meetingIds = this.getMeetingIdsOnRoom(roomConnections);
        return !!meetingIds.length && meetingIds.every(m => this.selectedMeetingIds.includes(m));

    }

    public getMeetingIdsOnRoom(roomConnections: IRoomConnections): number[] {
        return roomConnections.meetings?.length
            ? roomConnections.meetings.map(m => m.meetingId)
            : roomConnections.meetingIds?.length
                ? roomConnections.meetingIds
                : [];
    }

    @mobx.computed
    public get isAllMeetingsSelected(): boolean {
        const allMeetingsIds = this.roomsListWithConnections.reduce((acc, roomConnections) => {
            return [...new Set([...acc, ...this.getMeetingIdsOnRoom(roomConnections)])];
        }, []);

        return !!allMeetingsIds.length && allMeetingsIds.every(m => this.selectedMeetingIds.includes(m));
    }

    @mobx.action
    public selectMeetingsIdsByRoom(): void {
        this.roomsListWithConnections.forEach(room => {
            const meetingIds = this.getMeetingIdsOnRoom(room);
            this.selectedMeetingIds = [...new Set([...this.selectedMeetingIds, ...meetingIds])];
        });
    }

    @mobx.action
    public unSelectMeetingsIdsByRoom(): void {
        const updatedIds = [...this.selectedMeetingIds];
        this.roomsListWithConnections.forEach(room => {
            const meetingIds = this.getMeetingIdsOnRoom(room);
            meetingIds.forEach(id => {
                const currentIdIndex = updatedIds.indexOf(id);
                if (currentIdIndex !== -1) {
                    updatedIds.splice(currentIdIndex, 1);
                }
            });
            this.selectedMeetingIds = updatedIds;
        });
    }

    public checkRouteAfterBulkUpdate = async () => {
        const route = this.routingService.router.getState();
        if (isPathIncludesInCurrentRoute(Routes.ConnectionsList, route)) {
            await this.fetchRoomsWithConnections();
        }
    };

    @mobx.action.bound
    public async initOperatorsData(query: string = ''): Promise<void> {
        try {
            const { users = [] } = await this._usersDAO.searchDeliveryTeamMembers({
                page: 0,
                size: 100,
                query: query,
                activeOnly: true
            });
            mobx.runInAction(() => {
                this.operatorsListToAdd = users.map(user =>
                ({
                    fullName: `${user.firstName} ${user.lastName}`,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    user: user
                }));
            });
        } catch (error) {
            this.errorParsingService.extractMessage(error);
        }
    }

    @mobx.action
    private updateSingleMeetingInUI(payload: IUpdateSingleMeetingPayload) {
        const { roomId, meetingId, operators } = payload;
        const roomIndex = this.roomsListWithConnections.findIndex(r => r.roomId === roomId);
        const meetingIndex = this.roomsListWithConnections[roomIndex].meetings.findIndex(m => m.meetingId === meetingId);
        this.roomsListWithConnections[roomIndex].meetings[meetingIndex].operators = operators;
    }

    @mobx.action.bound
    public async addOperatorsToTheMeeting(
        currentOperators: IOperatorDetailsDTO[],
        operatorsList: IUserOperatorDTO[],
        meetingId: number,
        roomId: number
    ): Promise<void> {
        try {
            currentOperators.forEach(operator => operatorsList.forEach((item, index) => {
                if (item.email === operator.email) {
                    operatorsList.splice(index, 1);
                }
            }));
            const meetingOperators: IOperatorsDTO[] = operatorsList
                .reduce((_operators, operator) => {
                    // eslint-disable-next-line no-unused-vars
                    const { fullName, ...rest } = operator;
                    return _operators.concat(rest);
                }, []);
            const { operators } = await this.connectionsDAO.updateMeetingConnectionsWithSingleEntity({
                meetingId: meetingId,
                ...this.eventRequestParams,
                addedOperators: meetingOperators
            });

            this.updateSingleMeetingInUI({
                meetingId: meetingId,
                roomId: roomId,
                operators: operators
            });
        } catch (error) {
            alertServerError('Error', `${this.errorParsingService.extractMessage(error)} Please try again.`);
        }
    }

    public setOperatorRole(meetingId: number, roomId: number, email: string, role: OperatorRole): void {
        const selectedOperator = this.getSelectedOperator(roomId, meetingId, email);
        selectedOperator.operatorRole = selectedOperator.operatorRole === role ? null : role;

        this.updateSelectedOperator({
            roomId: roomId,
            meetingId: meetingId,
            operator: selectedOperator
        });
    }

    public removeOperator(meetingId: number, roomId: number, email: string): void {
        const selectedOperator = this.getSelectedOperator(roomId, meetingId, email);
        this.updateSelectedOperator({
            roomId: roomId,
            meetingId: meetingId,
            operator: selectedOperator
        }, true);
    }

    private getSelectedOperator(roomId: number, meetingId: number, email: string): IOperatorDetailsDTO {
        const roomIndex = this.roomsListWithConnections.findIndex(r => r.roomId === roomId);
        const meetingIndex = this.roomsListWithConnections[roomIndex].meetings.findIndex(m => m.meetingId === meetingId);
        return this.roomsListWithConnections[roomIndex]
            .meetings[meetingIndex].operators.find(o => o.email === email);
    }

    private async updateSelectedOperator(payload: IUpdateSingleMeetingPayload, shouldRemoveOperator?: boolean): Promise<void> {
        try {
            const { meetingId, roomId, operator } = payload;
            const { operators } = await this.connectionsDAO.updateMeetingConnectionsWithSingleEntity({
                meetingId: meetingId,
                ...this.eventRequestParams,
                ...(shouldRemoveOperator
                    ? { removedOperators: [operator] }
                    : { updatedOperators: [operator] })
            });

            this.updateSingleMeetingInUI({
                meetingId: meetingId,
                roomId: roomId,
                operators: operators
            });

        } catch (error) {
            if (error?.response.status === 404) {
                alertServerError('Error', `${this.errorParsingService.extractMessage(error)} Please refresh the page to see the latest data.`);
            } else {
                alertServerError('Error', this.errorParsingService.extractMessage(error));
            }
        }
    }

    public getKnovioReplayConnection = (roomMeetingConnections: IRoomMeetingConnectionsDTO): IConnectionDetailsDTO | undefined => {
        const now = new Date();
        const endTime = new Date(roomMeetingConnections.endDateTime);
        return isBefore(endTime, now)
            ? roomMeetingConnections.replayConnection
            : undefined
            ;
    };

    public trackConnectionsEventMixPanel = (eventName: string): void => {
        this.trackingService.get().track(eventName, {
            [eventName]: 'Yes'
        });
    };

    private closeMeetingConnectionPopUp = (meetingId: number): void => {
        this.editRegistrantPopupIds[meetingId]?.();
        this.meetingIdToSubWindowMap[meetingId]?.close();
    };

    public shouldDisableDeleteButton = (connection: IConnectionDetailsDTO): boolean => {
        return connection?.autoSyncEnabled && !this.executionRuntime.isReady();
    };
}

export { ConnectionsViewModel };
